import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 15 * 60 * 1000; // auto sign-out after 15 min of inactivity

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);
  const idleTimer = useRef(null);

  const logout = useCallback(async (opts = {}) => {
    const timedOut = opts && opts.timedOut === true;
    localStorage.removeItem("drill_school");
    localStorage.removeItem("sa_mode");
    localStorage.removeItem("last_activity");
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem("token");
    setUser(null);
    window.location.href = timedOut ? "/login?timeout=1" : "/login";
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setReady(true);
      return;
    }
    // If the app was left idle past the limit, drop the session instead of auto-restoring it.
    const last = Number(localStorage.getItem("last_activity") || 0);
    if (last && Date.now() - last > IDLE_MS) {
      localStorage.removeItem("token");
      localStorage.removeItem("last_activity");
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setReady(true));
  }, []);

  // Idle auto-logout while signed in: reset the timer on any user activity.
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      localStorage.setItem("last_activity", String(Date.now()));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => logout({ timedOut: true }), IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  const applySession = (data) => {
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("last_activity", String(Date.now()));
    setUser(data.user);
    return data.user;
  };

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    return applySession(data);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, applySession, logout, ready }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
